namespace $.$$ {

	export class $shm_hitalama_map_selectbox extends $.$shm_hitalama_map_selectbox {

		@ $mol_mem
		frozen_box( next?: {
			left: number
			top: number
			width: number
			height: number
		} | null ) {
			return next ?? null
		}
		drag_relatives?: readonly { top: number, left: number }[]
		resize_relatives?: readonly { top: number, left: number, width: number, height: number }[]

		Map_page(): $.$$.$shm_hitalama_map_page {
			return super.Map_page() as $.$$.$shm_hitalama_map_page
		}

		@ $mol_mem
		box() {
			return hitalama_map_selectbox_bounding_box(
				this.selected_links().map( link => this.Map_page().Widget( link ).Sub() as $shm_hitalama_map_widget_float )
			)
		}

		pointerdown( event: PointerEvent ) {
			if( !this.Map_page().map().can_change() ) return
			const target = event.target
			if( target instanceof Element && target.closest( '[role="button"],a,input,textarea,select,button' ) ) return

			this.set_relatives()
			this.drag_pointerdown( event )
		}

		@ $mol_action
		freeze_box() {
			const box = this.box()
			super.left_edge_x_stick( 0 )
			super.top_edge_y_stick( 0 )
			super.right_edge_x_stick( box.width )
			super.bottom_edge_y_stick( box.height )
			super.x( box.left )
			super.y( box.top )
			this.frozen_box( box )
			return box
		}

		@ $mol_action
		set_relatives() {
			const box = this.freeze_box()
			this.drag_relatives = this.calc_relative_coords( box.top, box.left )
		}

		on_drag_start( event: PointerEvent ) {
			$shm_hitalama_map_history.geometry_begin_many(
				this.selected_links().map( link => this.Map_page().widget_by_link( link ) ),
			)
			this.Map_page().selectbox_dragged( true )
		}

		on_drag_end( event?: PointerEvent | null ) {
			this.Map_page().selectbox_dragged( false )
			this.frozen_box( null )
			this.drag_relatives = undefined
			this.resize_relatives = undefined
			$shm_hitalama_map_history.geometry_end_map( this.Map_page().map() )
		}

		resize_start( next?: any ) {
			this.freeze_box()
			this.resize_relatives = this.calc_relative_rects()
			$shm_hitalama_map_history.geometry_begin_many(
				this.selected_links().map( link => this.Map_page().widget_by_link( link ) ),
			)
			this.Map_page().selectbox_dragged( true )
			super.resize_start( next )
		}

		@ $mol_mem
		top_edge_y( next?: number ): number {
			if( next === undefined ) return this.frozen_box() ? this.top_edge_y_stick() ?? 0 : 0
			const result = super.top_edge_y( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		bottom_edge_y( next?: number ): number {
			if( next === undefined ) return this.frozen_box() ? this.bottom_edge_y_stick() ?? 0 : this.box().height
			const result = super.bottom_edge_y( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		left_edge_x( next?: number ): number {
			if( next === undefined ) return this.frozen_box() ? this.left_edge_x_stick() ?? 0 : 0
			const result = super.left_edge_x( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		right_edge_x( next?: number ): number {
			if( next === undefined ) return this.frozen_box() ? this.right_edge_x_stick() ?? 0 : this.box().width
			const result = super.right_edge_x( next )
			this.resize_widgets()
			return result
		}

		@ $mol_mem
		top( next?: number ): number {
			const box = this.box()
			if( next === undefined && !this.frozen_box() ) return box.top

			const top = super.top( next )
			const relatives = this.drag_relatives
			if( !relatives ) return top

			this.selected_links().forEach( ( link, index ) => {
				const coords = relatives[ index ]
				if( !coords ) return
				this.Map_page().Widget( link ).Sub().top( top + coords.top )
			} )

			return top
		}

		@ $mol_mem
		left( next?: number ): number {
			const box = this.box()
			if( next === undefined && !this.frozen_box() ) return box.left

			const left = super.left( next )
			const relatives = this.drag_relatives
			if( !relatives ) return left

			this.selected_links().forEach( ( link, index ) => {
				const coords = relatives[ index ]
				if( !coords ) return
				this.Map_page().Widget( link ).Sub().left( left + coords.left )
			} )

			return left
		}

		@ $mol_mem
		width( next?: number ): number {
			const box = this.box()
			if( next === undefined && !this.frozen_box() ) return box.width
			if( next === undefined ) return super.width()

			const relatives = this.calc_relative_rects()
			const width = super.width( next )
			this.resize_widgets( relatives )
			return width
		}

		@ $mol_mem
		height( next?: number ): number {
			const box = this.box()
			if( next === undefined && !this.frozen_box() ) return box.height
			if( next === undefined ) return super.height()

			const relatives = this.calc_relative_rects()
			const height = super.height( next )
			this.resize_widgets( relatives )
			return height
		}

		calc_relative_coords( top: number, left: number ) {
			return this.selected_links().map( link => {
				const widget = this.Map_page().Widget( link ).Sub()
				return {
					top: widget.top() - top,
					left: widget.left() - left,
				}
			} )
		}

		resize_widgets( relatives = this.resize_relatives ) {
			if( !relatives ) return

			const top = this.top()
			const left = this.left()
			const width = this.width()
			const height = this.height()

			this.selected_links().forEach( ( link, index ) => {
				const rect = relatives[ index ]
				if( !rect ) return

				const widget = this.Map_page().Widget( link ).Sub()
				widget.top( top + rect.top * height )
				widget.left( left + rect.left * width )
				widget.width( rect.width * width )
				widget.height( rect.height * height )
			} )
		}

		calc_relative_rects() {
			const top = this.top()
			const left = this.left()
			const width = this.width() || 1
			const height = this.height() || 1

			return this.selected_links().map( link => {
				const widget = this.Map_page().Widget( link ).Sub()
				return {
					top: ( widget.top() - top ) / height,
					left: ( widget.left() - left ) / width,
					width: widget.width() / width,
					height: widget.height() / height,
				}
			} )
		}

		dragged_attr() {
			return this.dragged()
		}

		dragged(): ReturnType<ReturnType<$.$rise_resize[ "Drag_view" ]>[ "dragged" ]> {
			return this.Drag_view().dragged()
		}

		toolbar_transform() {
			return $shm_hitalama_map_float_toolbar_transform( this.zoom() )
		}

		toolbar(): readonly ( any )[] {
			const viewport = this.Map_page().Pane().Back().view_rect()
			const box = this.view_rect()
			if( viewport && box && ( box.right <= viewport.left || box.left >= viewport.right ) ) return []
			return super.toolbar()
		}

		toolbar_tools_max_width() {
			const viewport = this.Map_page().Pane().Back().view_rect()
			return viewport ? `${ Math.max( 1, viewport.width - 16 ) }px` : '100vw'
		}

		toolbar_tools_left() {
			const viewport = this.Map_page().Pane().Back().view_rect()
			const box = this.view_rect()
			const tools = this.Tools().view_rect()
			if( !viewport || !box || !tools ) return '0px'

			const margin = 8
			const width = Math.min( tools.width, viewport.width - margin * 2 )
			const left = Math.max( viewport.left + margin,
				Math.min( box.left, viewport.right - margin - width ) )
			return `${ ( left - box.left ) / this.zoom() }px`
		}

		count() {
			const counted = new Set< string >
			let count = 0

			this.selected_links().forEach( link => {
				this.Map_page().widget_by_link( link ).z_block().forEach( widget => {
					const id = widget.link_str()
					if( counted.has( id ) ) return
					counted.add( id )
					count += 1
				} )
			} )

			return count
		}

		count_title() {
			return String( this.count() )
		}

		zoom_for_resizers() {
			return Math.min( 5, this.zoom() )
		}

		resizer_transform() {
			return $shm_hitalama_map_float_resizer_transform( this.zoom() )
		}

		resizer_line_x_transform() {
			return $shm_hitalama_map_float_resizer_line_x_transform( this.zoom() )
		}

		resizer_line_y_transform() {
			return $shm_hitalama_map_float_resizer_line_y_transform( this.zoom() )
		}

		grouping() {
			this.Map_page().grouping()
		}

		delete() {
			this.Map_page().delete_selected()
		}

	}

	function hitalama_map_selectbox_bounding_box( widgets: $shm_hitalama_map_widget_float[] ) {
		const bounds = widgets.reduce( ( acc, w ) => ( {
			minLeft: Math.min( acc.minLeft, w.left() ),
			minTop: Math.min( acc.minTop, w.top() ),
			maxRight: Math.max( acc.maxRight, w.left() + w.width() ),
			maxBottom: Math.max( acc.maxBottom, w.top() + w.height() ),
		} ), {
			minLeft: Infinity,
			minTop: Infinity,
			maxRight: -Infinity,
			maxBottom: -Infinity,
		} )

		return {
			left: bounds.minLeft,
			top: bounds.minTop,
			width: bounds.maxRight - bounds.minLeft,
			height: bounds.maxBottom - bounds.minTop,
		}
	}

}
